// Footer.js
// -----------------------------------------------------------------------------
// Shared footer chrome for NHV. A warm, comprehensive multi-column footer on a
// deep brand-blue background:
//   - brand blurb + Give CTA
//   - contact details (InfoTile, inverted)
//   - a small sitemap/nav column
//   - a social icon row
//   - a copyright / attribution bar
//
// Zero-trust notes:
//   - All destinations (links, tel/mailto/maps/social) are closed in-file
//     constants: no external input drives any href.
//   - External links open in a new tab with rel="noopener noreferrer".
//   - Internal routes use react-router <Link>; the Give action is a modal.
// -----------------------------------------------------------------------------

import * as React from 'react';
import Box from '@mui/joy/Box';
import Stack from '@mui/joy/Stack';
import Grid from '@mui/joy/Grid';
import Typography from '@mui/joy/Typography';
import { Link as RouterLink } from 'react-router-dom';

import EmailIcon from '@mui/icons-material/Email';
import FacebookIcon from '@mui/icons-material/Facebook';
import FmdGoodIcon from '@mui/icons-material/FmdGood';
import PhoneIcon from '@mui/icons-material/Phone';
import YouTubeIcon from '@mui/icons-material/YouTube';
import InstagramIcon from '@mui/icons-material/Instagram';

import { Container, InfoTile, PrimaryButton } from './ui';
import GiveModal from './GiveModal';
import './styles/footer.css';

// --- Closed constant content (single source of truth) ----------------------
const PHONE_DISPLAY = '(972) 475-7511';
const PHONE_HREF = 'tel:9724757511';
const EMAIL = 'contactNHV@gmail.com';
const EMAIL_HREF = 'mailto:contactNHV@gmail.com';
const MAPS_HREF = 'https://maps.app.goo.gl/aptJc46XDx7rMGwG7';
const CURRENT_YEAR = new Date().getFullYear();

const SITEMAP_LINKS = [
  { label: 'Who We Are', to: '/leaders' },
  { label: 'Beliefs', to: '/beliefs' },
  { label: 'Ministries', to: '/ministries' },
  { label: 'Sermons', to: '/sermons' },
  { label: 'Plan a Visit', to: '/join' },
];

const SOCIAL_LINKS = [
  {
    label: 'Facebook',
    href: 'https://www.facebook.com/NewHorizonVineyard/',
    Icon: FacebookIcon,
  },
  {
    label: 'YouTube',
    href: 'https://www.youtube.com/@newhorizonvineyardchurch4635',
    Icon: YouTubeIcon,
  },
  {
    label: 'Instagram',
    href: 'https://www.instagram.com/nhvineyard/',
    Icon: InstagramIcon,
  },
];

const Footer = () => {
  return (
    <Box
      component="footer"
      sx={{
        bgcolor: 'primary.700', // deep brand blue
        color: 'common.white',
        mt: 'auto',
      }}
    >
      <Container sx={{ py: { xs: 5, md: 7 } }}>
        <Grid container spacing={{ xs: 4, md: 5 }}>
          {/* Brand blurb + Give CTA ----------------------------------------- */}
          <Grid xs={12} md={4}>
            <Typography level="h4" sx={{ color: 'common.white', mb: 1.25 }}>
              New Horizon Vineyard
            </Typography>
            <Typography
              level="body-md"
              sx={{ color: 'rgba(255,255,255,0.82)', mb: 2.5, maxWidth: '320px' }}
            >
              A warm, welcoming church family in Rowlett, TX. Wherever you are on
              your journey, there's a place for you here.
            </Typography>
            <PrimaryButton to="/join" color="accent" size="md">
              Plan a Visit
            </PrimaryButton>
          </Grid>

          {/* Contact details ------------------------------------------------ */}
          <Grid xs={12} sm={6} md={4}>
            <Typography level="title" sx={{ color: 'accent.300', mb: 2 }}>
              Get In Touch
            </Typography>
            <Stack spacing={2.25}>
              <InfoTile
                icon={<PhoneIcon fontSize="small" />}
                label="Call Us"
                value={PHONE_DISPLAY}
                href={PHONE_HREF}
                inverted
              />
              <InfoTile
                icon={<FmdGoodIcon fontSize="small" />}
                label="Find Us"
                value={
                  <>
                    4301 Dalrock Road
                    <br />
                    Rowlett, TX 75088
                  </>
                }
                href={MAPS_HREF}
                inverted
              />
              <InfoTile
                icon={<EmailIcon fontSize="small" />}
                label="Email Us"
                value={EMAIL}
                href={EMAIL_HREF}
                inverted
              />
            </Stack>
          </Grid>

          {/* Sitemap + social ---------------------------------------------- */}
          <Grid xs={12} sm={6} md={4}>
            <Typography level="title" sx={{ color: 'accent.300', mb: 2 }}>
              Explore
            </Typography>
            <Stack
              component="ul"
              spacing={1}
              sx={{ listStyle: 'none', m: 0, p: 0, mb: 3 }}
            >
              {SITEMAP_LINKS.map((item) => (
                <li key={item.to}>
                  <RouterLink className="footer-sitemap-link" to={item.to}>
                    {item.label}
                  </RouterLink>
                </li>
              ))}
              <li>
                <GiveModal linkClass="footer-sitemap-link" text="Give" />
              </li>
            </Stack>

            <Stack direction="row" spacing={1.25} aria-label="Social media">
              {SOCIAL_LINKS.map(({ label, href, Icon }) => (
                <a
                  key={label}
                  className="footer-social"
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                >
                  <Icon fontSize="medium" />
                </a>
              ))}
            </Stack>
          </Grid>
        </Grid>
      </Container>

      {/* Copyright / attribution bar --------------------------------------- */}
      <Box
        sx={{
          borderTop: '1px solid',
          borderColor: 'rgba(255,255,255,0.14)',
          bgcolor: 'primary.800',
        }}
      >
        <Container sx={{ py: 2.5 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={0.5}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
              textAlign: { xs: 'center', sm: 'left' },
            }}
          >
            <Typography level="body-sm" sx={{ color: 'rgba(255,255,255,0.78)' }}>
              Copyright {CURRENT_YEAR} New Horizon Vineyard Church
            </Typography>
            <Typography level="body-sm" sx={{ color: 'rgba(255,255,255,0.6)' }}>
              Powered by Olympus Code LLC
            </Typography>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
};

export default Footer;
